import { useState, useCallback } from 'react';
import { auth } from '../../../firebase/firebase.config';
import {
    addLeadForUser,
    updateLead,
    updateLeadStatus,
    updateLeadDetails,
    updateLeadTags,
    archiveLead,
    unarchiveLead,
    deleteLead,
    LEAD_STATUS,
    VALID_STATUSES,
} from '../../../firebase/leads.service';
import useAppStore from '../../../store/appStore';

/**
 * Custom hook for managing leads data.
 * Reads realtime data from centralized Zustand store (populated by realtimeSubscriptions).
 * Provides all CRUD operations.
 */
export const useLeadsData = () => {
    // ── Read directly from Zustand (single source of truth) ──
    const leads = useAppStore((s) => s.leads);
    const lastContactMap = useAppStore((s) => s.lastContactMap);
    const executionMap = useAppStore((s) => s.executionMap);
    const loadingLeads = useAppStore((s) => s.loadingStates.leads);

    const loading = loadingLeads || leads === null;
    const [fetchError, setFetchError] = useState('');

    // Add a new lead
    const handleAddLead = useCallback(async (leadData) => {
        try {
            const userId = auth.currentUser?.uid;
            if (!userId) throw new Error('Not authenticated');

            // Pass firstName/lastName to service - it will generate fullName
            await addLeadForUser(userId, {
                ...leadData,
                firstName: leadData.firstName || '',
                lastName: leadData.lastName || '',
                userId,
            });
            // Real-time listener will update state automatically
        } catch (error) {
            console.error('Error adding lead:', error);
            setFetchError(error.message || 'Error adding lead');
            throw error;
        }
    }, []);

    // Update lead status
    const handleUpdateStatus = useCallback(async (leadId, status) => {
        try {
            if (!VALID_STATUSES.includes(status)) {
                throw new Error(`Invalid status: ${status}`);
            }
            await updateLeadStatus(leadId, status);
            // Real-time listener will update state automatically
        } catch (error) {
            console.error('Error updating lead status:', error);
            setFetchError(error.message || 'Error updating status');
            throw error;
        }
    }, []);

    // Update lead details (name, email, phone, address, notes)
    const handleUpdateDetails = useCallback(async (leadId, details) => {
        try {
            await updateLeadDetails(leadId, details);
            // Real-time listener will update state automatically
        } catch (error) {
            console.error('Error updating lead details:', error);
            setFetchError(error.message || 'Error updating lead');
            throw error;
        }
    }, []);

    // Update lead tags
    const handleUpdateTags = useCallback(async (leadId, tags) => {
        try {
            await updateLeadTags(leadId, tags);
            // Real-time listener will update state automatically
        } catch (error) {
            console.error('Error updating lead tags:', error);
            setFetchError(error.message || 'Error updating tags');
            throw error;
        }
    }, []);

    // Archive a lead
    const handleArchiveLead = useCallback(async (leadId) => {
        try {
            await archiveLead(leadId);
            // Real-time listener will update state automatically
        } catch (error) {
            console.error('Error archiving lead:', error);
            setFetchError(error.message || 'Error archiving lead');
            throw error;
        }
    }, []);

    // Unarchive a lead
    const handleUnarchiveLead = useCallback(async (leadId) => {
        try {
            await unarchiveLead(leadId);
            // Real-time listener will update state automatically
        } catch (error) {
            console.error('Error unarchiving lead:', error);
            setFetchError(error.message || 'Error unarchiving lead');
            throw error;
        }
    }, []);

    // Delete a lead permanently
    const handleDeleteLead = useCallback(async (leadId) => {
        try {
            await deleteLead(leadId);
            // Real-time listener will update state automatically
        } catch (error) {
            console.error('Error deleting lead:', error);
            setFetchError(error.message || 'Error deleting lead');
            throw error;
        }
    }, []);

    // Generic update for any fields
    const handleUpdateLead = useCallback(async (leadId, updates) => {
        try {
            await updateLead(leadId, updates);
            // Real-time listener will update state automatically
        } catch (error) {
            console.error('Error updating lead:', error);
            setFetchError(error.message || 'Error updating lead');
            throw error;
        }
    }, []);

    return {
        leads: leads || [],
        loading,
        fetchError,
        lastContactMap: lastContactMap || {},
        executionMap: executionMap || new Map(),
        handleAddLead,
        handleUpdateStatus,
        handleUpdateDetails,
        handleUpdateTags,
        handleArchiveLead,
        handleUnarchiveLead,
        handleDeleteLead,
        handleUpdateLead,
        LEAD_STATUS,
        VALID_STATUSES,
    };
};

export default useLeadsData;
